import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import "../styles/Preferences.css";

const Preferences: React.FC = () => {
  const { t, i18n } = useTranslation("translation");
  // Helper alias to force t() to accept one argument.
  const translate = t as unknown as (key: string) => string;

  // State variables for preferences
  const [notificationPreference, setNotificationPreference] = useState("email");
  const [preferredLanguage, setPreferredLanguage] = useState("English");
  const [highContrastMode, setHighContrastMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Retrieve oneLoginSub from query string or localStorage
  const searchParams = new URLSearchParams(window.location.search);
  const oneLoginSub = localStorage.getItem("oneLoginSub") || "";

  useEffect(() => {
    if (!oneLoginSub) return;

    // Fetch user preferences from API only once on mount.
    fetch(
      `https://api.psanalytics.co.uk/ncjp/getPreferences.php?oneLoginSub=${encodeURIComponent(
        oneLoginSub
      )}`,
      { credentials: "include" }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.data) {
          setNotificationPreference(data.data.notificationPreference || "email");
          const fetchedLanguage = data.data.preferredLanguage || "English";
          setPreferredLanguage(fetchedLanguage);
          // Update the i18n language to reflect the API preference.
          i18n.changeLanguage(fetchedLanguage === "English" ? "en" : "cy");
          setHighContrastMode(data.data.highContrastMode === 1);
          if (data.data.highContrastMode === 1) {
            document.body.classList.add("high-contrast");
          }
        } else {
          console.error("Error retrieving preferences:", data.message);
          logError(`Unexpected API response format: ${JSON.stringify(data)}`);
          setErrorMessage(
            translate("dashboardInvitationMessage") ||
              "National My Police Portal is currently on an invitation-only basis. If you have made a service request or reported a crime, you will be invited to register for an account. New functionality will be introduced over the coming year."
          );
          return;
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching preferences:", err);
        setIsLoading(false);
      });
  }, [oneLoginSub]); // Only run on mount

  // Helper function to log errors
  const logError = (message: string) => {
    try {
      fetch("./error_log.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ error: message }),
      })
        .then(() => console.log("Error logged successfully:", message))
        .catch((err) =>
          console.error("Failed to log error to the server:", err)
        );
    } catch (e: any) {
      console.error("Error logging failed:", e.message);
    }
  };

  // When user selects a language via radio buttons.
  const handleLanguageChange = (lang: string) => {
    setPreferredLanguage(lang);
    // Immediately update the UI localization:
    i18n.changeLanguage(lang === "English" ? "en" : "cy");
  };

  const handleSavePreferences = () => {
    if (!oneLoginSub) {
      setSaveStatus(translate("errorNoUser") || "Error: Unable to determine user identity.");
      return;
    }

    const payload = {
      notificationPreference,
      preferredLanguage,
      highContrastMode: highContrastMode ? 1 : 0,
    };

    fetch(
      `https://api.psanalytics.co.uk/ncjp/updatePreferences.php?oneLoginSub=${encodeURIComponent(
        oneLoginSub
      )}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setSaveStatus(
            translate("preferencesUpdatedSuccessfully") ||
              "Preferences updated successfully!"
          );
        } else {
          setSaveStatus(
            (translate("errorUpdatingPreferences") || "Error updating preferences") +
              ": " +
              data.message
          );
        }
      })
      .catch((err) => {
        setSaveStatus(
          translate("errorUpdatingPreferences") ||
            "Error updating preferences. Please try again."
        );
      });
  };

  const handleToggleHighContrast = () => {
    const newContrastState = !highContrastMode;
    setHighContrastMode(newContrastState);
    if (newContrastState) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
  };

  if (isLoading) {
    return (
      <div>
        <Navbar />
        <main className="preferences-container">
          <p>{translate("loadingPreferences") || "Loading preferences..."}</p>
        </main>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <main className="preferences-container">
        <h1>{translate("myPreferences") || "My Preferences"}</h1>
        <section className="preferences-details">
          <h2>{translate("notificationPreferences") || "Notification Preferences"}</h2>
          <label>
            <input
              type="radio"
              name="notificationPreference"
              value="email"
              checked={notificationPreference === "email"}
              onChange={() => setNotificationPreference("email")}
            />
            {translate("email") || "Email"}
          </label>
          <label>
            <input
              type="radio"
              name="notificationPreference"
              value="sms"
              checked={notificationPreference === "sms"}
              onChange={() => setNotificationPreference("sms")}
            />
            {translate("sms") || "SMS"}
          </label>
          <label>
            <input
              type="radio"
              name="notificationPreference"
              value="both"
              checked={notificationPreference === "both"}
              onChange={() => setNotificationPreference("both")}
            />
            {translate("both") || "Both Email & SMS"}
          </label>

          <h2>{translate("preferredLanguage") || "Preferred Language"}</h2>
          <label>
            <input
              type="radio"
              name="preferredLanguage"
              value="English"
              checked={preferredLanguage === "English"}
              onChange={() => handleLanguageChange("English")}
            />
            {translate("english") || "English"}
          </label>
          <label>
            <input
              type="radio"
              name="preferredLanguage"
              value="Welsh"
              checked={preferredLanguage === "Welsh"}
              onChange={() => handleLanguageChange("Welsh")}
            />
            {translate("welsh") || "Welsh"}
          </label>

          <h2>{translate("accessibility") || "Accessibility"}</h2>
          <label>
            <input
              type="checkbox"
              checked={highContrastMode}
              onChange={handleToggleHighContrast}
            />
            {translate("enableHighContrastMode") || "Enable High Contrast Mode"}
          </label>

          <div className="button-group">
            <button className="govuk-button" onClick={handleSavePreferences}>
              {translate("savePreferences") || "Save Preferences"}
            </button>
            <Link to={`/`} className="govuk-button govuk-button--secondary cancel-button">
              {translate("cancel") || "Cancel"}
            </Link>
          </div>
          {saveStatus && (
            <p className={`save-status ${saveStatus.startsWith("Error") ? "error" : "success"}`}>
              {saveStatus}
            </p>
          )}
          {errorMessage && (
            <div className="alert alert-warning text-center my-4">
              {errorMessage}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Preferences;
